/* istanbul ignore file */

import {
  OrderWithContext,
  CustomerDeliveryAddress,
  OrderApiException,
  OrderApiError,
  OrderMeal,
  OrderMealRequest,
  OrderMealEntree,
  OrderMealRequestEntree,
  BasicOrderMealItem,
  OrderMealItem,
  UpdateDeliveryInfoOnOrderRequest,
  RecentOrFavoriteOrderMeal,
  RecentOrFavoriteEntree,
  RecentOrFavoriteItem
} from '@/models';
import { AxiosResponse, AxiosError } from 'axios';
import { GROUP_ORDER_MAX_PARTICIPANTS } from './order.service';

export function updateOrderWithApiResponseEtag(order: OrderWithContext, res: AxiosResponse) {
  order.etag = res.headers['etag'];
  return res;
}

export function fromDeliveryAddressToUpdateDeliveryInfoRequest(address: CustomerDeliveryAddress): UpdateDeliveryInfoOnOrderRequest {
  return {
    dropOffAddressLine1: address.streetAddress1 || '',
    dropOffAddressLine2: address.streetAddress2 || '',
    dropOffAddressCity: address.city || '',
    dropOffAddressState: address.state || '',
    dropOffAddressZipCode: address.postalCode || '',
    dropOffAddressCountryCode: address.countryCode || '',
    dropOffInstructions: address.notes || '',
    dropOffLongitude: address.longitude,
    dropOffLatitude: address.latitude,
    isContactlessDelivery: (address.contactlessDropoff !== undefined) ? address.contactlessDropoff : true
  };
}

// Convert the generic error to an OrderApiException if possible
export function generateOrderApiExceptionAndThrow(err: AxiosError): never {
  // Make sure the response shape is roughly what we expect order api errors to look like
  const errors = generateOrderApiErrors(err);

  const ex = new OrderApiException(errors);
  ex.stack = err.stack;
  ex.message = err.message;

  throw ex;
}

export function generateOrderApiErrors(err: AxiosError): OrderApiError[] {
  const errors = err.response && (err.response.data as OrderApiError[] | undefined);
  if (!errors || !errors.length || !errors[0].errorCode) {
    throw err;
  }

  return errors;
}

export function convertOrderMealToCreateMealRequest(meal: OrderMeal): OrderMealRequest {
  return {
    meal: {
      mealName: meal.mealName,
      entrees: meal.entrees ? mapMealEntreesToMealRequestEntrees(meal.entrees) : null,
      sides: meal.sides ? mapMealItemsToBasicMealItems(meal.sides) : null,
      drinks: meal.drinks ? mapMealItemsToBasicMealItems(meal.drinks) : null,
    },
  };
}

export function mapMealEntreesToMealRequestEntrees(mealEntrees: OrderMealEntree[]): OrderMealRequestEntree[] {
  return mealEntrees.map<OrderMealRequestEntree>(meal => ({
    customizationId: meal.customizationId,
    isUpSell: meal.isUpSell,
    menuItemId: meal.menuItemId,
    menuItemName: meal.menuItemName,
    quantity: meal.quantity,
    contents: meal.contents
      ? meal.contents.map<BasicOrderMealItem>(content => ({
        menuItemId: content.menuItemId,
        menuItemName: content.menuItemName,
        quantity: content.quantity,
        isUpSell: content.isUpSell,
        customizationId: content.customizationId,
      }))
      : [],
  }));
}

export function mapMealItemsToBasicMealItems(mealItem: OrderMealItem[]): BasicOrderMealItem[] {
  return mealItem.map(item => ({
    menuItemId: item.menuItemId,
    menuItemName: item.menuItemName,
    quantity: item.quantity,
    isUpSell: item.isUpSell,
    customizationId: item.customizationId,
  }));
}

export function convertRecentAndFavsMealToCreateMealRequest(meal: RecentOrFavoriteOrderMeal): OrderMealRequest {
  return {
    meal: {
      mealName: meal.mealName,
      entrees: meal.entrees ? mapRecentAndFavsEntreesToMealRequestEntrees(meal.entrees) : null,
      sides: meal.sides ? mapRecentAndFavsItemsToBasicMealItems(meal.sides) : null,
      drinks: meal.drinks ? mapRecentAndFavsItemsToBasicMealItems(meal.drinks) : null,
    },
  };
}

export function mapRecentAndFavsEntreesToMealRequestEntrees(mealEntrees: RecentOrFavoriteEntree[]): OrderMealRequestEntree[] {
  return mealEntrees.map<OrderMealRequestEntree>(meal => mapRecentAndFavEntreeToMealRequestEntree(meal));
}

export function convertRecentAndFavEntreeToOrderMealRequest(mealEntree: RecentOrFavoriteEntree): OrderMealRequest {
  return {
    meal: {
      mealName: '',
      entrees: [mapRecentAndFavEntreeToMealRequestEntree(mealEntree)],
      sides: [],
      drinks: [],
    },
  };
}

export function mapRecentAndFavEntreeToMealRequestEntree(mealEntree: RecentOrFavoriteEntree): OrderMealRequestEntree {
  return {
    customizationId: undefined,
    isUpSell: mealEntree.isUpSell,
    menuItemId: mealEntree.menuItemId,
    menuItemName: mealEntree.menuItemName,
    quantity: mealEntree.quantity,
    contents: mealEntree.contents
      ? mealEntree.contents.map<BasicOrderMealItem>(content => ({
        menuItemId: content.menuItemId,
        menuItemName: content.menuItemName,
        quantity: content.quantity,
        isUpSell: false,
        customizationId: content.customizationId,
      }))
      : []
  };
}

export function mapRecentAndFavsItemsToBasicMealItems(mealItem: RecentOrFavoriteItem[]): BasicOrderMealItem[] {
  return mealItem.map(item => ({
    menuItemId: item.menuItemId,
    menuItemName: item.menuItemName,
    quantity: item.quantity,
    isUpSell: item.isUpSell,
    customizationId: undefined,
  }));
}

export function hasMaxGroupOrderParticipantMeals(meals: OrderMeal[]): boolean {
  return getGroupOrderParticipantCount(meals) >= GROUP_ORDER_MAX_PARTICIPANTS;
}

export function getGroupOrderParticipantCount(meals: OrderMeal[]): number {
  return meals.reduce((acc, cur) => (cur.isHostMeal ? acc : acc + 1), 0);
}

